import React, { ButtonHTMLAttributes } from 'react';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { History } from 'history';
import { Chip } from '@mui/material';
import { CustomChipButton } from '../../../helpers/CustomChipButton';

type Targets = '_self' | '_blank' | '_parent' | '_top';

type ButtonStyles =
	| 'secondary'
	| 'warning'
	| 'info'
	| 'ordinary'
	| 'icon'
	| 'text'
	| 'text-small'
	| 'text-gray';

const execSummarySVG = (
	<svg
		width="12"
		height="12"
		viewBox="0 0 12 12"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
	>
		<g clipPath="url(#clip0_340_2252)">
			<path
				d="M2.25 8C2.388 8 2.5 8.112 2.5 8.25V9.75C2.5 9.888 2.388 10 2.25 10C2.112 10 2 9.888 2 9.75V8.25C2 8.112 2.112 8 2.25 8ZM3.5 5.75V9.75C3.5 9.888 3.612 10 3.75 10C3.888 10 4 9.888 4 9.75V5.75C4 5.612 3.888 5.5 3.75 5.5C3.612 5.5 3.5 5.612 3.5 5.75ZM5 6.75V9.75C5 9.888 5.112 10 5.25 10C5.388 10 5.5 9.888 5.5 9.75V6.75C5.5 6.612 5.388 6.5 5.25 6.5C5.112 6.5 5 6.612 5 6.75ZM12 7.375V11.1665C12 11.742 10.9955 12 10 12C9.0045 12 8 11.7425 8 11.1665V7.375C8 6.75 9.042 6.5135 10 6.5C10.9585 6.5135 12 6.75 12 7.375ZM11.501 9.2415C11.1155 9.415 10.5595 9.5 10 9.5C9.4405 9.5 8.885 9.4155 8.5 9.242V9.906C8.5585 10.0085 9.0715 10.25 10 10.25C10.9405 10.25 11.454 10.002 11.5015 9.9025L11.501 9.2415ZM11.4995 7.9725C11.1085 8.1585 10.545 8.25 9.999 8.25C9.453 8.25 8.89 8.1585 8.499 7.973V8.656C8.5575 8.7585 9.0705 9 9.999 9C10.937 9 11.45 8.7535 11.5 8.653L11.4995 7.9725ZM8.4995 7.375C8.5435 7.4825 9.051 7.75 9.9985 7.75C10.9595 7.75 11.4675 7.475 11.499 7.3705C11.468 7.275 10.9595 7 9.9985 7C9.0505 7 8.5435 7.2675 8.4995 7.375ZM11.501 11.1515V10.491C11.1155 10.665 10.559 10.75 9.9985 10.75C9.438 10.75 8.8835 10.6655 8.4985 10.492V11.1665C8.5415 11.25 9.0555 11.5 9.9985 11.5C10.9415 11.5 11.4555 11.2505 11.501 11.1515ZM6.9985 11.75C6.9985 11.888 6.8865 12 6.7485 12H2.25C1.0095 12 0 10.9905 0 9.75V2.25C0 1.0095 1.0095 0 2.25 0H5.0075C5.875 0 6.6915 0.338 7.306 0.952L9.048 2.6945C9.662 3.308 10 4.124 10 4.9925V5.25C10 5.388 9.888 5.5 9.75 5.5C9.612 5.5 9.5 5.388 9.5 5.25V4.9925C9.5 4.825 9.481 4.661 9.452 4.5H6.75C6.0605 4.5 5.5 3.939 5.5 3.25V0.548C5.3385 0.519 5.1745 0.5 5.0075 0.5H2.25C1.285 0.5 0.5 1.285 0.5 2.25V9.75C0.5 10.715 1.285 11.5 2.25 11.5H6.75C6.888 11.5 7 11.612 7 11.75H6.9985ZM6.9505 1.3055C6.675 1.0305 6.3505 0.826 5.9985 0.69V3.25C5.9985 3.6635 6.335 4 6.7485 4H9.309C9.1725 3.648 8.968 3.323 8.693 3.048L6.951 1.3055H6.9505Z"
				fill="#2424EC"
			/>
		</g>
		<defs>
			<clipPath id="clip0_340_2252">
				<rect width="12" height="12" fill="white" />
			</clipPath>
		</defs>
	</svg>
);

interface ButtonProps {
	type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
	buttonText?: string;
	icon?: React.ElementType;
	buttonClass?: string;
	buttonStyle?: ButtonStyles;
	large?: true;
	disabled?: boolean;
	forwardRef?: React.Ref<HTMLButtonElement>;
	onClick?: (e?: any) => void;
	toLeft?: true;
	toRight?: true;
	toCenter?: true;
	title?: string;
	to?: History.LocationDescriptor;
	target?: Targets;
	isChip?: boolean;
}

export const Button: React.FC<ButtonProps> = ({
	type = 'button',
	buttonText,
	buttonClass = '',
	buttonStyle,
	icon: Icon,
	forwardRef,
	onClick,
	large,
	disabled,
	toLeft,
	toRight,
	toCenter,
	children,
	title,
	to,
	target = '_self',
	isChip,
}) => {
	const linkRef = React.useRef<HTMLAnchorElement>(null);

	const classes = classNames('button', buttonClass, {
		[`button-${buttonStyle}`]: buttonStyle,
		'button-lg': large,
		'button-left': toLeft,
		'button-right': toRight,
		'button-center': toCenter,
	});
	const render = (
		<>
			{Icon && (
				<span className="button__icon">
					<Icon />
				</span>
			)}
			{buttonText}
			{children}
		</>
	);

	const handleButtonClick = () => {
		if (!to && onClick) {
			onClick();
		}
	};

	if (type === 'submit') {
		return (
			<button
				title={title}
				type="submit"
				className={classes}
				ref={forwardRef}
				onClick={onClick}
				disabled={disabled}
			>
				{render}
			</button>
		);
	}

	// this is basically CustomChipButton but for this specific component
	// also because I was doing it for the exec summary that's the icon I used, will need to be a prop if making this component more reusable
	if (isChip) {
		return (
			<>
				<Chip
					icon={execSummarySVG}
					label={
						to && !disabled ? (
							<Link to={to} ref={linkRef} target={target}>
								{render}
							</Link>
						) : (
							render
						)
					}
					onClick={handleButtonClick}
					sx={{
						borderRadius: '4px',
						width: 'auto',
						minHeight: 'auto',
						border: '0.8px solid #2424EC',
						color: '#2424EC',
						fontSize: '14px',
						padding: '20px 16px',
						backgroundColor: '#FFFFFF',
						cursor: 'pointer',
						'&&:hover': {
							backgroundColor: '#2424EC0D',
						},
					}}
					size="medium"
					variant="outlined"
				/>
			</>
		);
	}

	return (
		<button
			title={title}
			type="button"
			className={classes}
			onClick={handleButtonClick}
			disabled={disabled}
			style={to ? { padding: 0 } : {}}
		>
			{to && !disabled ? (
				<Link
					to={to}
					ref={linkRef}
					target={target}
					style={to ? { display: 'block', padding: '12px 16px' } : {}}
				>
					{render}
				</Link>
			) : (
				render
			)}
		</button>
	);
};
